import React, { useEffect, useId, useRef, useState } from "react";
import { Maximize2, X } from "lucide-react";
import DrawingEvidenceImage from "./DrawingEvidenceImage";
import "./SubmittedDrawingAnswer.css";

const sourceDescription = (source) => (source === "automatic-checkpoint"
  ? "captured automatically when time ran out (the last draft saved to StudyDen)"
  : "saved by the student");
const sourceLabel = (source) => {
  const description = sourceDescription(source);
  return description.charAt(0).toUpperCase() + description.slice(1);
};

const markLabel = (mark, maxMark) => {
  if (typeof mark === "number" && Number.isFinite(mark)) {
    return Number.isFinite(Number(maxMark)) ? `${mark} / ${maxMark}` : String(mark);
  }
  return mark === "N/A" ? "Not marked" : "Pending";
};

// Accessible enlarged view: the question, the drawing, any typed text, the
// mark and — only when the caller is allowed to show it — the feedback.
function EnlargedDrawing({ result, questionLabel, questionText, typedAnswer, mark, maxMark, feedback, onClose }) {
  const headingId = useId();
  const closeRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [onClose]);
  const keepFocusInside = (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...event.currentTarget.querySelectorAll("button, [href], [tabindex]:not([tabindex='-1'])")];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return (
    <div className="submitted-drawing-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="submitted-drawing-dialog" role="dialog" aria-modal="true" aria-labelledby={headingId} onKeyDown={keepFocusInside}>
        <div className="submitted-drawing-dialog-head">
          <h2 id={headingId}>{questionLabel ? `${questionLabel} — drawing answer` : "Drawing answer"}</h2>
          <button type="button" ref={closeRef} className="submitted-drawing-close" onClick={onClose} aria-label="Close enlarged drawing">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {questionText && <p className="submitted-drawing-question"><strong>Question:</strong> {questionText}</p>}
        <p className="submitted-drawing-source">{sourceLabel(result.drawingAnswer.source)}</p>
        <div className="submitted-drawing-large">
          <DrawingEvidenceImage
            attemptId={result.attemptId}
            questionId={result.questionId}
            version="submitted"
            revisionKey={result.drawingAnswer.revision}
            alt={questionText ? `Submitted drawing for: ${questionText}` : "Submitted drawing answer"}
          />
        </div>
        <dl className="submitted-drawing-details">
          <dt>Typed answer</dt>
          <dd>{typeof typedAnswer === "string" && typedAnswer.trim() ? typedAnswer : "No typed text — the drawing is the answer."}</dd>
          <dt>Mark</dt>
          <dd>{markLabel(mark, maxMark)}</dd>
          {typeof feedback === "string" && (
            <>
              <dt>Feedback</dt>
              <dd className="submitted-drawing-feedback">{feedback}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

// The drawing captured when the attempt was submitted. Results carry
// `drawingAnswer` only when the server-only drawing record has one; the image
// itself is delivered privately by the backend. `questionLabel` ("Question 3")
// must be the number the surrounding list shows.
export default function SubmittedDrawingAnswer({ result, questionLabel, questionText, typedAnswer, mark, maxMark, feedback }) {
  const [enlarged, setEnlarged] = useState(false);
  if (!result?.drawingAnswer || !result.attemptId || !result.questionId) return null;
  return (
    <div className="submitted-drawing">
      <p className="submitted-drawing-label">Drawing answer — {sourceDescription(result.drawingAnswer.source)}</p>
      <div className="submitted-drawing-frame">
        <DrawingEvidenceImage
          attemptId={result.attemptId}
          questionId={result.questionId}
          version="submitted"
          revisionKey={result.drawingAnswer.revision}
          alt="Submitted drawing answer"
        />
      </div>
      <button type="button" className="submitted-drawing-enlarge" onClick={() => setEnlarged(true)}
        aria-label={questionLabel ? `Enlarge drawing for ${questionLabel}` : undefined}>
        <Maximize2 size={15} aria-hidden="true" />Enlarge drawing
      </button>
      {enlarged && (
        <EnlargedDrawing
          result={result}
          questionLabel={questionLabel}
          questionText={questionText}
          typedAnswer={typedAnswer}
          mark={mark}
          maxMark={maxMark}
          feedback={feedback}
          onClose={() => setEnlarged(false)}
        />
      )}
    </div>
  );
}
